import { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, Rating, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import toast from 'react-hot-toast';
import { api } from '../../shared/service/dataprovider';
import Panel from '../../components/Panel';
import StatusChip from '../../components/StatusChip';

const fmtDate = d => (d ? moment(d).format('MMM D, YYYY h:mm A') : '—');

const SURVEY_LABELS = [
  { key: 'propertyTeam', label: 'Property Team' },
  { key: 'apartmentSatisfaction', label: 'Apartment Satisfaction' },
  { key: 'propertyCleanliness', label: 'Property Cleanliness' },
  { key: 'moveInProcess', label: 'Move-In Process' },
  { key: 'propertySatisfaction', label: 'Property Satisfaction' },
];

const EXPERIENCE_LABELS = [
  { key: 'teamCourteousOnTime', label: 'Team courteous and on time' },
  { key: 'apartmentReadyMetExpectations', label: 'Apartment ready / met expectations' },
  { key: 'noImmediateIssues', label: 'No immediate issues' },
  { key: 'properPaperworkAndInstructions', label: 'Paperwork and instructions' },
  { key: 'questionsAnswered', label: 'Questions answered' },
  { key: 'areaTrashFree', label: 'Area trash free' },
  { key: 'valetTrashBinProvided', label: 'Valet trash bin provided' },
];

const UNIT_AMENITY_LABELS = [
  { key: 'airConditioningHeating', label: 'Air Conditioning/Heating' },
  { key: 'appliances', label: 'Appliances' },
  { key: 'flooring', label: 'Carpeting/Tile/Vinyl' },
  { key: 'lightFixtures', label: 'Light Fixtures' },
  { key: 'paint', label: 'Paint' },
];

const yesNo = value => (value == null ? '—' : value ? 'Yes' : 'No');

async function downloadSurveyCsv({ propertyId }) {
  const params = new URLSearchParams();
  if (propertyId) params.set('propertyId', propertyId);
  const response = await api.get(`/v1/admin/move-in-condition/surveys/export?${params.toString()}`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `move-in-satisfaction-${moment().format('YYYY-MM-DD')}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

// Office counter-signature panel for the resident's move-in condition form.
// Shown on every lease (not affordable-only). The resident submits the form in
// the resident app; the site team reviews it here and counter-signs, which
// regenerates the PDF with the office signature and uploads the executed copy
// to ResMan.
export default function MoveInConditionPanel({ lease }) {
  const queryClient = useQueryClient();
  const queryKey = [`admin/lease/${lease.id}/move-in-condition`];
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => api.get(`/v1/admin/lease/${lease.id}/move-in-condition`),
    enabled: Boolean(lease?.id),
  });

  const info = data?.data;
  const survey = info?.satisfactionSurvey;

  const countersignMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/lease/${lease.id}/move-in-condition/countersign`),
    onSuccess: () => {
      toast.success('Move-in condition form counter-signed.');
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: err => {
      toast.error(err?.response?.data?.message ?? 'Could not counter-sign the form. Try again later.');
    },
  });

  const onExportCsv = async () => {
    try {
      setExporting(true);
      await downloadSurveyCsv({ propertyId: lease?.property?.id || lease?.propertyId });
      toast.success('Survey CSV downloaded.');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Could not export survey CSV.');
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }
  if (error) {
    return (
      <Typography sx={{ color: 'error.main' }}>{error?.response?.data?.message ?? 'Could not load the move-in condition form. Try again later.'}</Typography>
    );
  }

  if (!info?.submitted) {
    return (
      <Panel title="Move-In Condition">
        <Typography variant="body2" color="text.secondary">
          The resident has not submitted a move-in condition form yet. Once they complete it in the resident app, it will appear here for your review and
          counter-signature.
        </Typography>
      </Panel>
    );
  }

  const isCountersigned = Boolean(info.countersigned);
  const unsigned = info.origin === 'window_expired';
  const statusMeta = isCountersigned
    ? { tone: 'success', label: unsigned ? 'Property signed' : 'Counter-signed' }
    : { tone: 'warning', label: unsigned ? 'Needs property signature' : 'Awaiting office signature' };

  return (
    <Panel title="Move-In Condition" action={<StatusChip tone={statusMeta.tone} label={statusMeta.label} size="small" />}>
      <Stack gap={2}>
        <Stack gap={0.5}>
          <Typography variant="body2">
            <b>{unsigned ? 'Resident signature:' : 'Submitted by resident:'}</b> {unsigned ? 'Not signed' : fmtDate(info.createdAt)}
          </Typography>
          {!unsigned && info.pdfUrl && (
            <Typography variant="body2">
              <Link href={info.pdfUrl} target="_blank" rel="noopener noreferrer">
                View resident-submitted form (PDF)
              </Link>
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Resident copy in ResMan: {info.pdfResmanUploaded ? 'Uploaded' : 'Not uploaded'}
          </Typography>
        </Stack>

        {survey && (
          <Stack gap={1}>
            <Typography variant="body2">
              <b>Satisfaction survey average:</b> {survey.averageScore} / 5
            </Typography>
            {(survey.recommendLikelihood != null || survey.communityAmenitiesSatisfaction != null) && (
              <>
                <Typography variant="body2">
                  <b>Recommend likelihood:</b> {survey.recommendLikelihood ?? '—'} / 10
                </Typography>
                <Typography variant="body2">
                  <b>Community amenities:</b> {survey.communityAmenitiesSatisfaction ?? '—'} / 10
                </Typography>
                <Typography variant="body2">
                  <b>Keys ready on time:</b> {yesNo(survey.keysReadyOnTime)}
                </Typography>
                <Typography variant="body2">
                  <b>Mailbox key received:</b> {yesNo(survey.mailboxKeyReceived)}
                </Typography>
              </>
            )}
            {SURVEY_LABELS.map(item => (
              <Stack key={item.key} direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ minWidth: 180 }}>
                  {item.label}
                </Typography>
                <Rating value={survey[item.key] || 0} readOnly size="small" />
              </Stack>
            ))}
            {survey.experienceRatings &&
              EXPERIENCE_LABELS.map(item => (
                <Stack key={item.key} direction="row" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ minWidth: 220 }}>
                    {item.label}
                  </Typography>
                  <Rating value={survey.experienceRatings?.[item.key] || 0} readOnly size="small" />
                </Stack>
              ))}
            {survey.unitAmenityRatings &&
              UNIT_AMENITY_LABELS.map(item => (
                <Stack key={item.key} direction="row" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ minWidth: 220 }}>
                    {item.label}
                  </Typography>
                  <Rating value={survey.unitAmenityRatings?.[item.key] || 0} readOnly size="small" />
                </Stack>
              ))}
            {survey.comments && (
              <Typography variant="body2" color="text.secondary">
                <b>Comments:</b> {survey.comments}
              </Typography>
            )}
            <Button variant="outlined" size="small" onClick={onExportCsv} disabled={exporting} sx={{ alignSelf: 'flex-start' }}>
              {exporting ? 'Exporting…' : 'Download property survey CSV'}
            </Button>
          </Stack>
        )}

        {isCountersigned ? (
          <Stack gap={0.5}>
            <Typography variant="body2">
              <b>Counter-signed by:</b> {info.countersignerName || '—'} · {fmtDate(info.countersignedAt)}
            </Typography>
            {info.countersignedPdfUrl && (
              <Typography variant="body2">
                <Link href={info.countersignedPdfUrl} target="_blank" rel="noopener noreferrer">
                  View executed (counter-signed) form (PDF)
                </Link>
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Executed copy in ResMan: {info.countersignedPdfResmanUploaded ? 'Uploaded' : 'Not uploaded'}
            </Typography>
          </Stack>
        ) : (
          <Stack gap={1} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary">
              {unsigned
                ? 'The resident did not sign before the window closed. Sign this form for the office. The copy uploaded to ResMan will have the property signature and no resident signature.'
                : 'Review the resident’s form above, then counter-sign on behalf of the office. Your name and today’s date will be stamped on the form and the executed copy uploaded to ResMan.'}
            </Typography>
            <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={countersignMutation.isPending}>
              Counter-sign
            </Button>
          </Stack>
        )}
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Counter-sign move-in condition form?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {unsigned
              ? 'You are about to sign this unsigned move-in condition form for the office. Your name and today’s date will be stamped on the form, with no resident signature, and the copy will be uploaded to ResMan. This cannot be undone.'
              : 'You are about to counter-sign this move-in condition form on behalf of the office. Your name and today’s date will be stamped on the form, and the fully executed copy will be uploaded to ResMan. This cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={countersignMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => countersignMutation.mutate()} disabled={countersignMutation.isPending}>
            {countersignMutation.isPending ? 'Counter-signing…' : 'Counter-sign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Panel>
  );
}
